(() => {
  if (window.customElements.get('homepage-trusted-creators')) {
    return;
  }

  class HomepageTrustedCreators extends HTMLElement {
    connectedCallback() {
      window.customElements.whenDefined('scroll-carousel').then(() => {
        if (this.isConnected) {
          this.initialize();
        }
      });
    }

    disconnectedCallback() {
      this.cleanupExpandedVideo({ restoreFocus: false });

      if (this.dialog?.open && typeof this.dialog.close === 'function') {
        this.dialog.close();
      }

      this.abortController?.abort();
      this.viewportObserver?.disconnect();
      this.pauseAll({ unloadYouTube: true });
      this.initialized = false;
    }

    initialize() {
      if (this.initialized) {
        return;
      }

      this.carousel = this.querySelector('scroll-carousel');
      this.cards = Array.from(this.querySelectorAll('[data-trusted-creator-card]'));
      this.dialog = this.querySelector('[data-trusted-creator-dialog]');
      this.dialogStage = this.dialog?.querySelector('[data-trusted-creator-dialog-stage]');
      this.dialogTitle = this.dialog?.querySelector('[data-trusted-creator-dialog-title]');
      this.dialogPlayButton = this.dialog?.querySelector('[data-trusted-creator-dialog-play]');
      this.dialogSoundButton = this.dialog?.querySelector('[data-trusted-creator-dialog-sound]');
      this.dialogScrubber = this.dialog?.querySelector('[data-trusted-creator-dialog-scrubber]');

      if (!this.carousel || this.cards.length === 0) {
        return;
      }

      this.initialized = true;
      this.selectedIndex = 0;
      this.isNearViewport = false;
      this.userPausedCard = null;
      this.completedCard = null;
      this.abortController = new AbortController();
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      const { signal } = this.abortController;

      this.addEventListener('carousel:select', (event) => {
        if (event.target !== this.carousel) {
          return;
        }

        this.selectCard(Number(event.detail?.index) || 0);
      }, { signal });

      this.cards.forEach((card, index) => {
        card.querySelector('[data-trusted-creator-play]')?.addEventListener('click', () => {
          this.selectCarouselCard(index);

          if (!this.hasNativeVideo(card)) {
            this.userPausedCard = null;
            this.completedCard = null;
            this.selectCard(index, { play: false, userInitiated: true });
            this.mountYouTube(card);
            return;
          }

          const video = this.mountNativeVideo(card);
          const wasPlaying = Boolean(video && !video.paused && !video.ended);

          if (wasPlaying) {
            this.userPausedCard = card;
            this.pauseCard(card);
            return;
          }

          this.userPausedCard = null;
          this.completedCard = null;
          this.selectCard(index, { play: true, userInitiated: true });
        }, { signal });

        card.querySelector('[data-trusted-creator-mute]')?.addEventListener('click', (event) => {
          if (!this.hasNativeVideo(card)) {
            return;
          }

          const button = event.currentTarget;

          if (button.getAttribute('aria-expanded') === 'true') {
            this.closeVolumeControl(card);
            return;
          }

          this.selectCarouselCard(index);
          this.userPausedCard = null;
          this.completedCard = null;
          this.selectCard(index, { play: true, userInitiated: true });

          const video = this.mountNativeVideo(card);

          if (video) {
            const savedVolume = Number(card.dataset.trustedCreatorVolume);
            const restoredVolume = savedVolume > 0
              ? savedVolume
              : (video.volume > 0 ? video.volume : 1);

            if (video.muted || video.volume === 0) {
              video.volume = restoredVolume;
              video.muted = false;
            }

            this.openVolumeControl(card);
            this.syncSoundButton(card, video);
          }
        }, { signal });

        card.querySelector('[data-trusted-creator-volume]')?.addEventListener('input', (event) => {
          const video = this.mountNativeVideo(card);

          if (!video) {
            return;
          }

          const volume = Math.max(0, Math.min(1, Number(event.currentTarget.value)));
          video.volume = volume;
          video.muted = volume === 0;

          if (volume > 0) {
            card.dataset.trustedCreatorVolume = String(volume);
          }

          this.syncSoundButton(card, video);
        }, { signal });

        card.querySelector('[data-trusted-creator-expand]')?.addEventListener('click', (event) => {
          if (!this.hasNativeVideo(card)) {
            return;
          }

          this.selectCarouselCard(index);
          this.openExpandedVideo(card, event.currentTarget);
        }, { signal });
      });

      document.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const isVolumeInteraction = Boolean(
          target
          && this.contains(target)
          && target.closest('[data-trusted-creator-mute], [data-trusted-creator-volume-popover]'),
        );

        if (!isVolumeInteraction) {
          this.closeAllVolumeControls();
        }
      }, { signal });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') {
          return;
        }

        const openButton = this.querySelector('[data-trusted-creator-mute][aria-expanded="true"]');

        if (!openButton) {
          return;
        }

        event.preventDefault();
        this.closeVolumeControl(openButton.closest('[data-trusted-creator-card]'), { restoreFocus: true });
      }, { signal });

      this.dialog?.querySelector('[data-trusted-creator-dialog-close]')?.addEventListener('click', () => {
        this.closeExpandedVideo();
      }, { signal });

      this.dialogPlayButton?.addEventListener('click', () => {
        this.toggleDialogPlayback();
      }, { signal });

      this.dialogSoundButton?.addEventListener('click', () => {
        this.toggleDialogSound();
      }, { signal });

      this.dialogScrubber?.addEventListener('input', (event) => {
        this.scrubDialogVideo(event.currentTarget);
      }, { signal });

      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) {
          this.closeExpandedVideo();
        }
      }, { signal });

      this.dialog?.addEventListener('close', () => {
        this.cleanupExpandedVideo();
      }, { signal });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.dialogVideo?.pause();
          this.pauseAll({ unloadYouTube: true });
        } else if (this.isNearViewport && !this.motionQuery.matches) {
          this.resumeSelectedCard();
        }
      }, { signal });

      this.motionQuery.addEventListener('change', () => {
        if (this.motionQuery.matches) {
          this.dialogVideo?.pause();
          this.pauseAll({ unloadYouTube: true });
        } else if (this.isNearViewport) {
          this.resumeSelectedCard();
        }
      }, { signal });

      if ('IntersectionObserver' in window) {
        this.viewportObserver = new IntersectionObserver((entries) => {
          this.isNearViewport = Boolean(entries[0]?.isIntersecting);

          if (!this.isNearViewport) {
            this.pauseAll({ unloadYouTube: true });
            return;
          }

          if (!this.motionQuery.matches) {
            this.resumeSelectedCard();
          }
        }, {
          rootMargin: '80px 0px',
          threshold: 0.05,
        });

        this.viewportObserver.observe(this);
      } else {
        this.isNearViewport = true;

        if (!this.motionQuery.matches) {
          this.resumeSelectedCard();
        }
      }

      this.selectCard(0, { play: false });
    }

    selectCarouselCard(index) {
      if (this.selectedIndex !== index && typeof this.carousel.select === 'function') {
        this.carousel.select(index);
      }
    }

    selectCard(index, { play = true, userInitiated = false } = {}) {
      const safeIndex = Math.max(0, Math.min(index, this.cards.length - 1));
      const previousCard = this.cards[this.selectedIndex];
      const selectedCard = this.cards[safeIndex];

      if (selectedCard !== previousCard) {
        this.userPausedCard = null;
        this.completedCard = null;
        this.closeAllVolumeControls();
      }

      this.selectedIndex = safeIndex;

      this.cards.forEach((card, cardIndex) => {
        card.classList.toggle('is-selected', cardIndex === safeIndex);

        if (cardIndex !== safeIndex) {
          this.pauseCard(card);
          this.unmountYouTube(card);
        }
      });

      if (
        !play
        || !this.hasNativeVideo(selectedCard)
        || (!userInitiated && this.userPausedCard === selectedCard)
        || (!userInitiated && this.completedCard === selectedCard)
        || (!userInitiated && (!this.isNearViewport || this.motionQuery.matches))
      ) {
        return;
      }

      this.playCard(safeIndex);
    }

    resumeSelectedCard() {
      const selectedCard = this.cards[this.selectedIndex];

      if (
        this.hasNativeVideo(selectedCard)
        && selectedCard !== this.userPausedCard
        && selectedCard !== this.completedCard
      ) {
        this.playCard(this.selectedIndex);
      }
    }

    hasNativeVideo(card) {
      return Boolean(
        card
        && !card.classList.contains('has-native-error')
        && (card.querySelector('video') || card.querySelector('[data-trusted-creator-native-template]')),
      );
    }

    mountNativeVideo(card) {
      if (this.dialogCard === card && this.dialogVideo) {
        return this.dialogVideo;
      }

      const mountedVideo = card?.querySelector('video');

      if (mountedVideo) {
        return mountedVideo;
      }

      const template = card?.querySelector('[data-trusted-creator-native-template]');
      const media = card?.querySelector('.trusted-creators__media');

      if (!template || !media || card.classList.contains('has-native-error')) {
        return null;
      }

      const fragment = template.content.cloneNode(true);
      const video = fragment.querySelector('video');

      if (!video) {
        return null;
      }

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.disablePictureInPicture = true;
      video.disableRemotePlayback = true;
      video.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback');
      video.setAttribute('disablepictureinpicture', '');
      video.setAttribute('disableremoteplayback', '');
      media.appendChild(fragment);
      card.classList.add('is-mounted');
      this.syncSoundButton(card, video);
      this.syncPlayButton(card, video);

      video.addEventListener('loadstart', () => card.classList.add('is-loading'));
      video.addEventListener('waiting', () => card.classList.add('is-loading'));
      video.addEventListener('canplay', () => card.classList.remove('is-loading'));
      video.addEventListener('playing', () => {
        card.classList.remove('is-loading');
        card.classList.add('is-playing');
        this.syncPlayButton(card, video);
        this.syncDialogPlayback(video);
      });
      video.addEventListener('pause', () => {
        card.classList.remove('is-loading', 'is-playing');
        this.syncPlayButton(card, video);
        this.syncDialogPlayback(video);
      });
      video.addEventListener('ended', () => {
        this.completedCard = card;
        card.classList.remove('is-loading', 'is-playing');
        this.syncPlayButton(card, video);
        this.syncDialogPlayback(video);
        this.syncDialogTimeline(video);
      });
      video.addEventListener('timeupdate', () => this.syncDialogTimeline(video));
      video.addEventListener('durationchange', () => this.syncDialogTimeline(video));
      video.addEventListener('loadedmetadata', () => this.syncDialogTimeline(video));
      video.addEventListener('volumechange', () => {
        this.syncSoundButton(card, video);
        this.syncDialogSound(video);
      });
      video.addEventListener('error', () => this.activateYouTubeFallback(card, video), { once: true });

      return video;
    }

    playCard(index) {
      const card = this.cards[index];

      if (!card || document.hidden || !this.hasNativeVideo(card)) {
        return;
      }

      this.cards.forEach((otherCard, otherIndex) => {
        if (otherIndex !== index) {
          this.pauseCard(otherCard);
          this.unmountYouTube(otherCard);
        }
      });

      const video = this.mountNativeVideo(card);

      if (!video) {
        return;
      }

      card.classList.remove('has-native-error');
      card.classList.add('is-loading');

      if (video.ended) {
        video.currentTime = 0;
      }

      const playPromise = video.play();

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          card.classList.remove('is-loading', 'is-playing');
          this.syncPlayButton(card, video);
        });
      }
    }

    pauseCard(card) {
      const video = card?.querySelector('video');

      if (video && !video.paused) {
        video.pause();
      }

      card?.classList.remove('is-loading', 'is-playing');

      if (video) {
        this.syncPlayButton(card, video);
      }
    }

    pauseAll({ unloadYouTube = false } = {}) {
      this.closeAllVolumeControls();

      this.cards?.forEach((card) => {
        this.pauseCard(card);

        if (unloadYouTube) {
          this.unmountYouTube(card);
        }
      });
    }

    mountYouTube(card) {
      const mountedIframe = card?.querySelector('.trusted-creators__iframe');

      if (mountedIframe) {
        return mountedIframe;
      }

      const template = card?.querySelector('[data-trusted-creator-youtube-template]');
      const media = card?.querySelector('.trusted-creators__media');

      if (!template || !media) {
        return null;
      }

      this.cards.forEach((otherCard) => {
        this.pauseCard(otherCard);

        if (otherCard !== card) {
          this.unmountYouTube(otherCard);
        }
      });

      const fragment = template.content.cloneNode(true);
      const iframe = fragment.querySelector('iframe');

      if (!iframe) {
        return null;
      }

      media.appendChild(fragment);
      card.classList.add('is-youtube-mounted');
      return iframe;
    }

    unmountYouTube(card) {
      card?.querySelector('.trusted-creators__iframe')?.remove();
      card?.classList.remove('is-youtube-mounted');
    }

    activateYouTubeFallback(card, video) {
      const hasYouTubeFallback = Boolean(card.querySelector('[data-trusted-creator-youtube-template]'));
      const playButton = card.querySelector('[data-trusted-creator-play]');
      const failedInDialog = video === this.dialogVideo;

      this.closeVolumeControl(card);

      video?.remove();

      if (failedInDialog) {
        this.dialogVideo = null;
        this.closeExpandedVideo();
      }

      card.classList.remove('is-loading', 'is-playing', 'is-mounted');
      card.classList.add('has-native-error');

      if (!playButton) {
        return;
      }

      if (hasYouTubeFallback) {
        playButton.disabled = false;
        playButton.setAttribute('aria-pressed', 'false');
        playButton.setAttribute('aria-label', `Redă pe YouTube: ${card.dataset.trustedCreatorLabel || 'videoclip'}`);
      } else {
        playButton.disabled = true;
        playButton.setAttribute('aria-label', 'Videoclipul nu este disponibil');
      }
    }

    openExpandedVideo(card, opener) {
      if (!this.dialog || !this.dialogStage || this.dialog.open) {
        return;
      }

      this.closeAllVolumeControls();

      const video = this.mountNativeVideo(card);
      const media = card.querySelector('.trusted-creators__media');

      if (!video || !media) {
        return;
      }

      this.userPausedCard = card;
      this.completedCard = null;
      this.pauseCard(card);
      this.dialogCard = card;
      this.dialogMedia = media;
      this.dialogOpener = opener;
      this.dialogVideo = video;
      opener?.setAttribute('aria-expanded', 'true');

      const creatorName = card.querySelector('.trusted-creators__name')?.textContent?.trim();
      const label = card.dataset.trustedCreatorLabel || 'Videoclip creator';

      if (this.dialogTitle) {
        this.dialogTitle.textContent = creatorName ? `Videoclip cu ${creatorName}` : label;
      }

      video.controls = false;
      video.disablePictureInPicture = true;
      video.disableRemotePlayback = true;
      video.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback nofullscreen');
      video.setAttribute('disablepictureinpicture', '');
      video.setAttribute('disableremoteplayback', '');
      video.classList.add('qk-creator-videos__dialog-video');
      video.removeAttribute('aria-hidden');
      video.setAttribute('aria-label', label);
      video.setAttribute('tabindex', '-1');
      this.dialogStage.appendChild(video);
      this.syncDialogControls(video);
      document.documentElement.classList.add('qk-creator-video-dialog-open');

      try {
        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }
      } catch (_error) {
        this.cleanupExpandedVideo();
        return;
      }

      if (video.ended) {
        video.currentTime = 0;
      }

      const playPromise = video.play();

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    closeExpandedVideo() {
      if (!this.dialog) {
        return;
      }

      if (typeof this.dialog.close === 'function' && (this.dialog.open || this.dialog.hasAttribute('open'))) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.cleanupExpandedVideo();
      }
    }

    cleanupExpandedVideo({ restoreFocus = true } = {}) {
      const video = this.dialogVideo;
      const card = this.dialogCard;
      const opener = this.dialogOpener;

      if (video) {
        video.pause();
        video.controls = false;
        video.classList.remove('qk-creator-videos__dialog-video');
        video.setAttribute('aria-hidden', 'true');
        video.setAttribute('tabindex', '-1');
        video.removeAttribute('aria-label');
        this.dialogMedia?.appendChild(video);

        if (card) {
          this.userPausedCard = card;
          this.syncPlayButton(card, video);
          this.syncSoundButton(card, video);
        }
      }

      document.documentElement.classList.remove('qk-creator-video-dialog-open');
      this.dialogVideo = null;
      this.dialogCard = null;
      this.dialogMedia = null;
      this.dialogOpener = null;

      opener?.setAttribute('aria-expanded', 'false');

      if (restoreFocus && opener?.isConnected) {
        opener.focus();
      }
    }

    toggleDialogPlayback() {
      const video = this.dialogVideo;

      if (!video) {
        return;
      }

      if (!video.paused && !video.ended) {
        video.pause();
        return;
      }

      if (video.ended) {
        video.currentTime = 0;
      }

      this.completedCard = null;
      const playPromise = video.play();

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    toggleDialogSound() {
      const video = this.dialogVideo;

      if (!video) {
        return;
      }

      video.muted = !video.muted;
      this.syncDialogSound(video);

      if (this.dialogCard) {
        this.syncSoundButton(this.dialogCard, video);
      }
    }

    scrubDialogVideo(scrubber) {
      const video = this.dialogVideo;
      const duration = video?.duration;

      if (!video || scrubber !== this.dialogScrubber || !Number.isFinite(duration) || duration <= 0) {
        return;
      }

      const maximum = Number(scrubber.max) || 1000;
      const progress = Math.max(0, Math.min(1, Number(scrubber.value) / maximum));
      video.currentTime = progress * duration;

      if (progress < 1) {
        this.completedCard = null;
      }

      this.syncDialogTimeline(video);
    }

    syncDialogControls(video) {
      this.syncDialogPlayback(video);
      this.syncDialogSound(video);
      this.syncDialogTimeline(video);
    }

    syncDialogPlayback(video) {
      if (!this.dialogPlayButton || video !== this.dialogVideo) {
        return;
      }

      const isPlaying = !video.paused && !video.ended;
      this.dialogPlayButton.setAttribute('aria-pressed', String(isPlaying));
      this.dialogPlayButton.setAttribute('aria-label', isPlaying ? 'Pune videoclipul pe pauză' : 'Redă videoclipul');
    }

    syncDialogSound(video) {
      if (!this.dialogSoundButton || video !== this.dialogVideo) {
        return;
      }

      const isAudible = !video.muted;
      this.dialogSoundButton.setAttribute('aria-pressed', String(isAudible));
      this.dialogSoundButton.setAttribute('aria-label', isAudible ? 'Oprește sunetul' : 'Pornește sunetul');
    }

    syncDialogTimeline(video) {
      if (!this.dialogScrubber || video !== this.dialogVideo) {
        return;
      }

      const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
      const currentTime = duration > 0 ? Math.max(0, Math.min(video.currentTime, duration)) : 0;
      const progress = duration > 0 ? currentTime / duration : 0;
      const maximum = Number(this.dialogScrubber.max) || 1000;

      this.dialogScrubber.disabled = duration <= 0;
      this.dialogScrubber.value = String(Math.round(progress * maximum));
      this.dialogScrubber.style.setProperty('--qk-creator-video-progress', `${progress * 100}%`);
      this.dialogScrubber.setAttribute(
        'aria-valuetext',
        `${this.formatDialogTime(currentTime)} din ${this.formatDialogTime(duration)}`,
      );
    }

    formatDialogTime(seconds) {
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
      const minutes = Math.floor(safeSeconds / 60);
      const remainingSeconds = String(safeSeconds % 60).padStart(2, '0');
      return `${minutes}:${remainingSeconds}`;
    }

    openVolumeControl(card) {
      const popover = card?.querySelector('[data-trusted-creator-volume-popover]');
      const button = card?.querySelector('[data-trusted-creator-mute]');

      if (!popover || !button) {
        return;
      }

      this.closeAllVolumeControls(card);
      popover.hidden = false;
      button.setAttribute('aria-expanded', 'true');
    }

    closeVolumeControl(card, { restoreFocus = false } = {}) {
      const popover = card?.querySelector('[data-trusted-creator-volume-popover]');
      const button = card?.querySelector('[data-trusted-creator-mute]');

      if (!popover || !button) {
        return;
      }

      const wasOpen = button.getAttribute('aria-expanded') === 'true';
      popover.hidden = true;
      button.setAttribute('aria-expanded', 'false');

      if (restoreFocus && wasOpen && button.isConnected) {
        button.focus();
      }
    }

    closeAllVolumeControls(exceptCard = null) {
      this.cards?.forEach((card) => {
        if (card !== exceptCard) {
          this.closeVolumeControl(card);
        }
      });
    }

    syncSoundButton(card, video) {
      const button = card?.querySelector('[data-trusted-creator-mute]');

      if (!button || !video) {
        return;
      }

      const creatorName = card.querySelector('.trusted-creators__name')?.textContent?.trim() || 'creator';
      const volume = Math.max(0, Math.min(1, Number(video.volume)));
      const isAudible = !video.muted && volume > 0;
      const audibleVolume = isAudible ? volume : 0;
      const volumePercent = Math.round(audibleVolume * 100);
      const slider = card.querySelector('[data-trusted-creator-volume]');

      if (isAudible) {
        card.dataset.trustedCreatorVolume = String(volume);
      }

      button.dataset.trustedCreatorAudible = String(isAudible);
      button.setAttribute(
        'aria-label',
        `Reglează volumul pentru ${creatorName} (${isAudible ? `${volumePercent}%` : 'oprit'})`,
      );

      if (slider) {
        slider.value = String(audibleVolume);
        slider.style.setProperty('--trusted-creators-volume', `${volumePercent}%`);
        slider.setAttribute('aria-valuetext', `${volumePercent}%`);
      }
    }

    syncPlayButton(card, video) {
      const button = card?.querySelector('[data-trusted-creator-play]');

      if (!button || !video) {
        return;
      }

      const label = card.dataset.trustedCreatorLabel || 'videoclip';
      const isPlaying = !video.paused && !video.ended;
      button.setAttribute('aria-pressed', String(isPlaying));
      button.setAttribute('aria-label', `${isPlaying ? 'Pauză' : 'Redă'}: ${label}`);
    }
  }

  window.customElements.define('homepage-trusted-creators', HomepageTrustedCreators);
})();
